// ── BCHNostr — Verify Business (owner-facing) ─────────────────────
//
// Lives in the EXISTING business management interface. Lets the business
// owner pay the (admin-configurable) verification fee in BCH and become a
// Verified BCH Business, discoverable through BCH Local.
//
// Reuses, never rebuilds:
//   · the existing business identity (the acting pubkey's kind 30499),
//   · the existing tip wallet (useTipWallet.sendBCH → PLATFORM_FEE_ADDRESS),
//   · the existing on-chain audit (paymentAudit.auditClaim) to CONFIRM the
//     payment before we treat verification as pending,
//   · the existing grant/badge model (badge goes live when a trusted issuer
//     grants — automated backend or operator, exactly like paid NIP-05),
//   · the existing 30499 profile as the merchant-discovery signal (we stamp
//     the verified marker onto it so BCH Local sees it).
//
// The owner never sees the words "heartbeat", "grant", or "Nostr event".

import React, { useState } from 'react';
import {
  BadgeCheck, ShieldCheck, Loader2, Wallet, MapPin, Clock, AlertTriangle, Check, ExternalLink,
} from 'lucide-react';
import { useTipWallet } from '../hooks/useTipWallet';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useBusinessVerification, useBusinessVerifyFeeUsd } from '../hooks/useBusinessVerification';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { auditClaim } from '../lib/paymentAudit';
import { PLATFORM_FEE_ADDRESS } from '../lib/platform';
import {
  buildVerifyClaimEvent, verifyFeeSats, formatVerifyDate,
  pendingExpiryNotice, BUSINESS_VERIFY_PERIOD_DAYS,
} from '../lib/businessVerify';
import { buildBusinessEvent, BUSINESS_KIND, type BusinessProfile } from '../lib/business';

const BCH_LOCAL_URL = 'https://bchlocal.com';

/** Re-publish the EXISTING 30499 profile, stamping the verified marker. */
async function stampVerifiedMarker(
  publish: ReturnType<typeof useNostrPublish>['publish'],
  business: BusinessProfile,
  verifiedUntil: number,
) {
  const { tags, content } = buildBusinessEvent({
    name: business.name,
    category: business.category,
    proType: business.proType,
    location: business.location,
    bch: business.bch,
    geohash: business.geohash,
    onlineOnly: business.onlineOnly,
    content: {
      proType: business.proType,
      category: business.category,
      tagline: business.tagline,
      description: business.description,
      phone: business.phone,
      email: business.email,
      website: business.website,
      hours: business.hours,
      onlineOnly: business.onlineOnly,
      acceptsCashtokens: business.acceptsCashtokens,
      socials: business.socials,
      gallery: business.gallery,
    },
    verifiedUntil,
  });
  await publish({ kind: BUSINESS_KIND, tags, content, created_at: Math.floor(Date.now() / 1000) });
}

export default function VerifyBusinessSection({ pubkey }: { pubkey: string }) {
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { business } = useBusinessProfile(pubkey);
  const { feeUsd } = useBusinessVerifyFeeUsd();
  const verification = useBusinessVerification(pubkey);

  const [step, setStep] = useState<'idle' | 'paying' | 'confirming' | 'publishing' | 'error'>('idle');
  const [error, setError] = useState('');
  const [walletInitializing, setWalletInitializing] = useState(false);
  const [markerDone, setMarkerDone] = useState(false);

  const price = getBCHPrice();
  const feeSats = verifyFeeSats(feeUsd, price);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = feeSats > 0 && walletBalance >= feeSats + 300;

  const status = verification.status;

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch { /* surfaced below */ }
    setWalletInitializing(false);
  };

  const handlePay = async () => {
    if (!canAfford || feeSats <= 0) return;
    setStep('paying');
    setError('');
    try {
      if (!tipWallet.ready) await tipWallet.init();

      // 1. Pay the fee to the platform fee address (existing rails).
      const txid = await tipWallet.sendBCH(PLATFORM_FEE_ADDRESS, BigInt(feeSats));

      // 2. Confirm the payment ON-CHAIN before recording a claim, so we never
      //    treat verification as pending before payment is confirmed. Reuses
      //    the existing audit. Unconfirmed txs return 'not-found' briefly;
      //    that's fine — the claim records the txid and the backend/operator
      //    confirms when issuing the grant, exactly like paid NIP-05.
      setStep('confirming');
      if (txid) {
        const audit = await auditClaim(
          { name: pubkey, pubkey, claimedAt: Math.floor(Date.now() / 1000), txid, amountSats: feeSats },
          PLATFORM_FEE_ADDRESS,
          feeSats,
        );
        if (audit.verdict === 'wrong-recipient' || audit.verdict === 'underpaid') {
          throw new Error(
            audit.verdict === 'underpaid'
              ? 'The payment was for less than the verification fee.'
              : 'The payment did not reach the verification address.',
          );
        }
        // 'not-found' / 'unavailable' are transient (unconfirmed / explorer
        // hiccup) — proceed and let the grant step confirm.
      }

      // 3. Publish the CLAIM (receipt with the txid). This does NOT grant the
      //    badge — a trusted issuer's grant does — but it moves the business
      //    to PENDING and is the auditable record of payment.
      setStep('publishing');
      const { kind, tags, content } = buildVerifyClaimEvent(txid || '', feeSats);
      await publish({ kind, tags, content, created_at: Math.floor(Date.now() / 1000) });

      tipWallet.refreshBalance();
      verification.refetch();
      setStep('idle');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed. Please try again.');
      setStep('error');
    }
  };

  // Once verified, offer to stamp the discovery marker onto the profile so
  // BCH Local picks the business up. Done automatically the first time.
  const needsMarker = status === 'VERIFIED'
    && business
    && business.verifiedUntil < verification.expiresAt
    && !markerDone;

  const handleStampMarker = async () => {
    if (!business) return;
    setStep('publishing');
    try {
      await stampVerifiedMarker(publish, business, verification.expiresAt);
      setMarkerDone(true);
      setStep('idle');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your listing.');
      setStep('error');
    }
  };

  const busy = step === 'paying' || step === 'confirming' || step === 'publishing';

  return (
    <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-emerald-500/10">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold flex items-center gap-1.5">
            Verify Your Business
            {status === 'VERIFIED' && <BadgeCheck className="w-4 h-4 text-emerald-500" />}
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Become a verified BCH business, discoverable through BCH Local.
          </p>
        </div>
      </div>

      {/* ── VERIFIED ── */}
      {status === 'VERIFIED' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2.5">
            <BadgeCheck className="w-5 h-5 text-emerald-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Verified BCH Business</p>
              <p className="text-[10px] text-muted-foreground">
                Verified {formatVerifyDate(verification.startAt)} · Expires {formatVerifyDate(verification.expiresAt)}
              </p>
            </div>
          </div>

          {/* Renewal reminder at 30 / 7 / 1 days before expiry. */}
          {pendingExpiryNotice(verification) !== null && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2.5">
              <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                  Verification expires in {Math.max(0, verification.daysUntilExpiry)} day{verification.daysUntilExpiry === 1 ? '' : 's'}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Renew to stay a Verified BCH Business and keep your BCH Local listing active.
                </p>
                <button
                  onClick={handlePay}
                  disabled={busy || !canAfford}
                  className="mt-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-semibold disabled:opacity-50 flex items-center gap-1.5"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />}
                  Renew for ${feeUsd}
                </button>
                {!canAfford && feeSats > 0 && (
                  <p className="text-[10px] text-red-500 mt-1">Load your wallet to renew.</p>
                )}
              </div>
            </div>
          )}

          {needsMarker && (
            <button
              onClick={handleStampMarker}
              disabled={busy}
              className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
              Publish to BCH Local
            </button>
          )}

          <a
            href={BCH_LOCAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2 rounded-lg border border-border text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-primary/40 hover:text-primary transition-colors"
          >
            <MapPin className="w-3.5 h-3.5" /> View on BCH Local <ExternalLink className="w-3 h-3" />
          </a>

          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Verification means this business completed BCHNostr's verification process. It is not a
            government or legal verification, and does not guarantee the business is currently
            accepting BCH.
          </p>
        </div>
      )}

      {/* ── EXPIRED ── */}
      {status === 'EXPIRED' && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2.5">
          <Clock className="w-4 h-4 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Your verification expired on {formatVerifyDate(verification.expiresAt)}. Renew below to
            stay discoverable on BCH Local.
          </p>
        </div>
      )}

      {/* ── PENDING ── */}
      {status === 'PENDING' && (
        <div className="flex items-center gap-2 rounded-lg bg-blue-500/10 border border-blue-500/20 px-3 py-2.5">
          <Loader2 className="w-4 h-4 text-blue-500 shrink-0 animate-spin" />
          <p className="text-xs text-blue-600 dark:text-blue-400">
            Payment received — confirming. Your Verified badge activates shortly, usually within a
            few minutes.
          </p>
        </div>
      )}

      {/* ── Pay / renew (UNVERIFIED or EXPIRED) ── */}
      {(status === 'UNVERIFIED' || status === 'EXPIRED') && (
        <div className="rounded-lg bg-background border border-border p-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Verification fee</span>
            <span className="font-bold font-mono">
              ${feeUsd}/year{feeSats > 0 ? ` · ${feeSats.toLocaleString()} sats` : ''}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Pay in BCH from your in-app wallet. Verification lasts {BUSINESS_VERIFY_PERIOD_DAYS} days.
          </p>

          {!tipWallet.ready ? (
            <button
              onClick={handleInitWallet}
              disabled={walletInitializing}
              className="w-full py-2 rounded-lg bg-muted text-xs font-medium flex items-center justify-center gap-1.5"
            >
              {walletInitializing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
              {walletInitializing ? 'Loading…' : 'Load Wallet'}
            </button>
          ) : (
            <>
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>Wallet balance</span>
                <span className={`font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                  {walletBalance.toLocaleString()} sats
                </span>
              </div>
              <button
                onClick={handlePay}
                disabled={busy || !canAfford}
                className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  canAfford
                    ? 'bg-gradient-to-r from-primary to-emerald-500 text-white hover:opacity-90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {step === 'paying' ? <><Loader2 className="w-4 h-4 animate-spin" /> Paying…</>
                  : step === 'confirming' ? <><Loader2 className="w-4 h-4 animate-spin" /> Confirming…</>
                  : step === 'publishing' ? <><Loader2 className="w-4 h-4 animate-spin" /> Finishing…</>
                  : <><BadgeCheck className="w-4 h-4" /> {status === 'EXPIRED' ? 'Renew' : 'Verify'} for ${feeUsd}</>}
              </button>
              {!canAfford && feeSats > 0 && (
                <p className="text-[10px] text-red-500">Not enough funds. Deposit to your wallet first.</p>
              )}
              {feeSats <= 0 && (
                <p className="text-[10px] text-amber-500">Fetching the current BCH price…</p>
              )}
            </>
          )}
        </div>
      )}

      {step === 'error' && error && (
        <p className="flex items-start gap-1.5 text-xs text-red-500">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
        </p>
      )}
      {markerDone && (
        <p className="flex items-center gap-1.5 text-xs text-emerald-500">
          <Check className="w-3.5 h-3.5" /> Your listing is now published to BCH Local.
        </p>
      )}
    </div>
  );
}
